import { CDN_URL, WORKER_URL } from '../../config.js';
import { fetchCache } from '../../utils/fetchCache.js';
import { withSfwQueryString } from '../../utils/nsfwTags.js';
import { getNsfwEnabled } from '../../utils/userPrefs.js';
import { applyTranslations, getCurrentLanguage } from '../../translations.js';
import { showError, showSuccess } from '../../utils/UnifiedNotificationSystem.js';
import {
  getAuthToken,
  getUserCredits,
  showFreeAccountModalIfNeeded,
  syncSpriteCredits,
} from '../videoScheduleApi.js';
import {
  buildLocalCreditResult,
  computeVideoUnitCost,
  interpolateCreditTemplate,
  videoDurationBounds,
} from '../creditCostApi.js';
import { mountVideoLoraRows, SPRITE_VIDEO_LORA_MAX, STANDALONE_VIDEO_LORA_MAX } from '../videoLoraRows.js';
import { bindMediaCropPanel } from '../ui/mediaCropPanel.js';
import {
  formatSpriteNameForDisplay,
  getSpriteSmallImagePath,
  spriteKey,
} from '../utils/spriteDisplay.js';
import { VIDEO_KINDS, VIDEO_SOURCES } from './parseCreateVideoConfig.js';
import { notifyVideoContainerStarting, waitForQueuedVideo } from '../waitForQueuedVideo.js';
import { adoptWaitEstimate, remainingWaitSeconds } from '../waitEstimate.js';
import {
  fetchStandaloneImageObjectUrl,
} from '../standaloneHdUpgrade.js';
import {
  fetchStandaloneVideoObjectUrl,
  scheduleStandaloneVideo,
  uploadStandaloneVideoFrame,
} from '../standaloneMediaApi.js';
import { consumeSimilarStandaloneVideo } from '../similarStandaloneVideo.js';
import { bindGenerationSeedControl, parseGenerationSeed } from '../generationSeed.js';
import { attachFreeVideoImagePickerToTrigger } from '../ui/freeVideoImagePickerModal.js';
import { requestAiGenerateField } from '../generationApi.js';
import { openFreeVideoWandModal, FREE_VIDEO_PROMPT_MAX } from './freeVideoWandModal.js';
import { setupCharacterCount } from '../utils/validation.js';
import { fitStandaloneVideoFrame } from './standaloneVideoFrame.js';
import { sfwModerationMessage } from '../sfwModerationError.js';

let videoSeedControl = null;

const getT = (key, fallback) => {
  const lang = getCurrentLanguage() || 'en';
  const t = window.translations?.[lang]?.['create-video'] ?? window.translations?.en?.['create-video'];
  return t?.[key] ?? fallback;
};

const queryEls = () => ({
  kindTabs: Array.from(document.querySelectorAll('[data-tablist="video-kind"] [role="tab"]')),
  sourceTabs: Array.from(document.querySelectorAll('[data-tablist="video-source"] [role="tab"]')),
  spritePanel: document.getElementById('panel-video-sprite'),
  freePanel: document.getElementById('panel-video-free'),
  sourceSprite: document.getElementById('free-video-source-sprite'),
  sourceGallery: document.getElementById('free-video-source-gallery'),
  sourceUpload: document.getElementById('free-video-source-upload'),
  charSelect: document.getElementById('receiver-char-free-video'),
  outfitSelect: document.getElementById('receiver-outfit-free-video'),
  spriteGrid: document.getElementById('sprite-grid-free-video'),
  spriteStatus: document.getElementById('sprite-status-free-video'),
  spriteSearch: document.getElementById('sprite-action-search-free-video'),
  galleryPickerTrigger: document.getElementById('free-video-gallery-picker-trigger'),
  galleryPickerLabel: document.querySelector('#free-video-gallery-picker-trigger .search-dropdown__label'),
  gallerySection: document.getElementById('free-video-gallery-section'),
  galleryPreview: document.getElementById('free-video-gallery-preview'),
  galleryPreviewImg: document.getElementById('free-video-gallery-preview-img'),
  uploadSection: document.getElementById('free-video-upload-section'),
  uploadPreview: document.getElementById('free-video-upload-preview'),
  uploadPreviewImg: document.getElementById('free-video-upload-preview-img'),
  uploadInput: document.getElementById('free-video-upload-input'),
  cropPanel: document.getElementById('free-video-crop-panel'),
  cropViewportHost: document.getElementById('free-video-crop-viewport-host'),
  cropViewport: document.getElementById('free-video-crop-viewport'),
  cropImage: document.getElementById('free-video-crop-image'),
  cropZoom: document.getElementById('free-video-crop-zoom'),
  cropApply: document.getElementById('free-video-crop-apply'),
  cropCancel: document.getElementById('free-video-crop-cancel'),
  prompt: document.getElementById('camera-text-free-video'),
  promptTitle: document.getElementById('free-video-prompt-title'),
  creditLine: document.querySelector('#credit-info-free-video [data-credit-line]'),
  submit: document.getElementById('validate-free-video'),
  previewSection: document.getElementById('free-video-preview-section'),
  previewCard: document.getElementById('free-video-preview-card'),
  eta: document.getElementById('free-video-eta'),
  loraTitle: document.getElementById('free-video-lora-title'),
  loraRows: document.getElementById('free-video-lora-rows'),
  addLora: document.getElementById('free-video-add-lora'),
  loraLimit: document.getElementById('free-video-lora-limit'),
  seed: document.getElementById('free-video-seed'),
  seedLock: document.getElementById('free-video-seed-lock'),
  seedReroll: document.getElementById('free-video-seed-reroll'),
  wand: document.getElementById('ai-wand-free-video'),
});

const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

let videoLoras = null;

const videoLoraCap = (source) => (
  source === VIDEO_SOURCES.SPRITE ? SPRITE_VIDEO_LORA_MAX : STANDALONE_VIDEO_LORA_MAX
);

const readVideoLora = ({ strict = false } = {}) => {
  if (!getNsfwEnabled()) return null;
  return videoLoras?.collect({ strict }) ?? null;
};

const setVideoKind = (els, kind) => {
  const isFree = kind === VIDEO_KINDS.FREE;
  els.kindTabs.forEach((tab) => {
    const isActive = tab.id === `video-kind-${kind}`;
    tab.classList.toggle('create-mode-tab--active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.setAttribute('tabindex', isActive ? '0' : '-1');
  });
  els.spritePanel?.classList.toggle('hidden', isFree);
  if (els.spritePanel) els.spritePanel.hidden = isFree;
  els.freePanel?.classList.toggle('hidden', !isFree);
  if (els.freePanel) els.freePanel.hidden = !isFree;
};

const setVideoSource = (els, source) => {
  const panels = {
    [VIDEO_SOURCES.SPRITE]: els.sourceSprite,
    [VIDEO_SOURCES.GALLERY]: els.sourceGallery,
    [VIDEO_SOURCES.UPLOAD]: els.sourceUpload,
  };
  els.sourceTabs.forEach((tab) => {
    const isActive = tab.id === `video-source-${source}`;
    tab.classList.toggle('create-mode-tab--active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.setAttribute('tabindex', isActive ? '0' : '-1');
  });
  Object.entries(panels).forEach(([key, panel]) => {
    const show = key === source;
    panel?.classList.toggle('hidden', !show);
    if (panel) panel.hidden = !show;
  });
  videoLoras?.setMax(videoLoraCap(source));
  const promptStep = source === VIDEO_SOURCES.SPRITE ? '3' : '2';
  if (els.promptTitle) {
    els.promptTitle.setAttribute('data-n', promptStep);
    const template = getT('section_video_prompt', '{n}. Enter your prompt');
    els.promptTitle.textContent = String(template).replaceAll('{n}', promptStep);
  }
};

let crop = null;
let selectedSprite = null;
let selectedGalleryKey = null;
let appliedFrame = null;
let appliedFrameSize = null;
let pendingFrameSize = null;
let waitAbort = null;
let videoEtaTimer = null;
let cropObjectUrl = null;
let cropObjectUrlOwned = false;

const stopVideoEta = (els) => {
  if (videoEtaTimer) {
    clearInterval(videoEtaTimer);
    videoEtaTimer = null;
  }
  els?.eta?.classList.add('hidden');
};

const startVideoEta = (els, jobKey) => {
  if (videoEtaTimer) {
    clearInterval(videoEtaTimer);
    videoEtaTimer = null;
  }
  let waitEstimate = null;
  els.eta?.classList.remove('hidden');
  const renderEta = () => {
    if (!els.eta) return;
    const remaining = remainingWaitSeconds(waitEstimate);
    els.eta.textContent = remaining != null && remaining > 0
      ? getT('free_video_eta', 'About {seconds}s remaining').replace('{seconds}', String(remaining))
      : getT('creating_video', 'Creating...');
  };
  videoEtaTimer = setInterval(renderEta, 1000);
  renderEta();
  return (data) => {
    waitEstimate = adoptWaitEstimate(waitEstimate, data);
    renderEta();
    notifyVideoContainerStarting(data, jobKey);
  };
};

const previewElsForSource = (els, source) => {
  if (source === VIDEO_SOURCES.GALLERY) {
    return { preview: els.galleryPreview, img: els.galleryPreviewImg, section: els.gallerySection };
  }
  return { preview: els.uploadPreview, img: els.uploadPreviewImg, section: els.uploadSection };
};

const clearAppliedPreview = (els) => {
  [els.galleryPreviewImg, els.uploadPreviewImg].forEach((img) => {
    if (!img) return;
    img.removeAttribute('src');
    img.classList.add('is-hidden');
  });
};

const releaseOwnedCropUrl = () => {
  const ownedUrl = cropObjectUrlOwned ? cropObjectUrl : null;
  cropObjectUrl = null;
  cropObjectUrlOwned = false;
  if (ownedUrl) URL.revokeObjectURL(ownedUrl);
};

const applyVideoFrameBox = (preview, frame) => {
  if (!preview || !frame?.width || !frame?.height) return;
  preview.style.setProperty('--video-frame-ratio', String(frame.width / frame.height));
};

const clearVideoFrameBox = (preview) => {
  preview?.style.removeProperty('--video-frame-ratio');
};

const probeImageSize = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
  image.onerror = () => reject(new Error('Failed to load image'));
  image.src = src;
});

const hideCrop = (els, { clearApplied = false } = {}) => {
  crop?.destroy();
  els.cropPanel?.classList.add('is-hidden');
  els.galleryPreview?.classList.remove('is-cropping');
  els.uploadPreview?.classList.remove('is-cropping');
  if (els.cropViewportHost && els.cropViewport && els.cropViewport.parentElement !== els.cropViewportHost) {
    els.cropViewportHost.appendChild(els.cropViewport);
  }
  releaseOwnedCropUrl();
  pendingFrameSize = null;
  if (els.uploadInput) els.uploadInput.value = '';
  if (clearApplied) {
    appliedFrame = null;
    appliedFrameSize = null;
    clearVideoFrameBox(els.galleryPreview);
    clearVideoFrameBox(els.uploadPreview);
    clearAppliedPreview(els);
  }
};

const reportCropLoadError = (error) => {
  console.error('[freeVideoFlow] crop load failed', error);
  showError(getT('free_video_crop_failed', 'Failed to apply cropped image.'));
};

const showCropInSection = async (els, source, src, { ownedBlob = false } = {}) => {
  const { preview, section } = previewElsForSource(els, source);
  const previousUrl = cropObjectUrl;
  const previousOwned = cropObjectUrlOwned;
  cropObjectUrl = null;
  cropObjectUrlOwned = false;
  hideCrop(els, { clearApplied: true });
  if (previousOwned && previousUrl && previousUrl !== src) URL.revokeObjectURL(previousUrl);
  const isBlob = typeof src === 'string' && src.startsWith('blob:');
  cropObjectUrl = isBlob ? src : null;
  cropObjectUrlOwned = Boolean(cropObjectUrl && ownedBlob);
  const probed = await probeImageSize(src);
  const frame = fitStandaloneVideoFrame(probed.width, probed.height);
  pendingFrameSize = frame;
  applyVideoFrameBox(preview, frame);
  if (preview && els.cropViewport) {
    preview.classList.add('is-cropping');
    preview.appendChild(els.cropViewport);
  }
  if (section && els.cropPanel) section.appendChild(els.cropPanel);
  els.cropPanel?.classList.remove('is-hidden');
  await crop.load(src);
};

const applyCrop = async (els) => {
  if (!crop?.active || !pendingFrameSize) return;
  const frame = pendingFrameSize;
  const dataUrl = await crop.exportDataUrl({
    outputWidth: frame.width,
    outputHeight: frame.height,
  });
  appliedFrame = dataUrl;
  appliedFrameSize = frame;
  const { preview, img } = previewElsForSource(els, sourceState);
  if (img) {
    img.src = dataUrl;
    img.classList.remove('is-hidden');
  }
  hideCrop(els);
  preview?.classList.remove('is-cropping');
};

const updateCredits = (els) => {
  const maxLoras = videoLoraCap(sourceState);
  const lora = readVideoLora();
  const overLora = Boolean(videoLoras?.overLimit());
  const cost = computeVideoUnitCost({
    operation: 'video_create',
    userVideoLora: lora,
    maxLoras,
    durationSeconds: readDurationSeconds(),
  });
  if (els.loraLimit) {
    els.loraLimit.hidden = !overLora;
    els.loraLimit.classList.toggle('hidden', !overLora);
    if (overLora) els.loraLimit.textContent = videoLoras.limitMessage();
  }
  const template = els.creditLine?.getAttribute('data-translate')
    ? getT('credits_per_video', '1 video = {cost} credits (You have {available} credits)')
    : els.creditLine?.textContent;
  const { label } = {
    label: interpolateCreditTemplate(template || '1 video = {cost} credits (You have {available} credits)', {
      cost,
      available: getUserCredits(),
      total: cost,
      count: 1,
    }),
  };
  if (els.creditLine) els.creditLine.textContent = label;
  const hasPrompt = Boolean(els.prompt?.value?.trim());
  const hasSource = hasFreeVideoSource();
  const hasEnough = cost <= getUserCredits();
  if (els.submit) els.submit.disabled = !(hasPrompt && hasSource && hasEnough && !overLora);
  if (els.wand && !els.wand.classList.contains('is-busy')) els.wand.disabled = !hasSource;
};

let sourceState = VIDEO_SOURCES.GALLERY;

const readLoopClip = () => {
  const selected = document.querySelector('#free-video-wand-loop-field input[name="free-video-wand-loop"]:checked');
  return selected instanceof HTMLInputElement && selected.value === 'yes';
};

const syncDurationOptions = () => {
  const select = document.getElementById('free-video-duration');
  if (!(select instanceof HTMLSelectElement)) return;
  const { min, max } = videoDurationBounds();
  const previous = Number.parseInt(select.value, 10);
  select.replaceChildren();
  for (let seconds = min; seconds <= max; seconds += 1) {
    const option = document.createElement('option');
    option.value = String(seconds);
    option.textContent = String(seconds);
    select.append(option);
  }
  const next = Number.isInteger(previous) && previous >= min && previous <= max
    ? previous
    : Math.min(max, Math.max(min, 5));
  select.value = String(next);
};

const readDurationSeconds = () => {
  const { min, max } = videoDurationBounds();
  const raw = Number.parseInt(document.getElementById('free-video-duration')?.value, 10);
  if (Number.isInteger(raw) && raw >= min && raw <= max) return raw;
  return Math.min(max, Math.max(min, 5));
};

const hasFreeVideoSource = () => {
  if (sourceState === VIDEO_SOURCES.SPRITE) return Boolean(selectedSprite);
  return Boolean(appliedFrame && (sourceState === VIDEO_SOURCES.UPLOAD || selectedGalleryKey));
};

const loadFreeSprites = async (els) => {
  const charId = els.charSelect?.value;
  const outfitId = els.outfitSelect?.value;
  if (!charId || outfitId === '' || outfitId == null) {
    if (els.spriteGrid) els.spriteGrid.replaceChildren();
    if (els.spriteStatus) els.spriteStatus.textContent = getT('select_character_for_videos', 'Select a character to view images');
    return;
  }
  if (els.spriteStatus) els.spriteStatus.textContent = getT('loading', 'Loading…');
  try {
    const url = withSfwQueryString(`${WORKER_URL}/get_character_sprites?character_id=${charId}&outfit_id=${outfitId}`);
    const data = await fetchCache.fetchJSON(url);
    const sprites = (data.sprites || []).filter((sprite) => [1, 4].includes(Number(sprite.status)));
    els.spriteGrid.replaceChildren();
    sprites.forEach((sprite) => {
      const item = {
        ...sprite,
        char_id: sprite.char_id ?? charId,
        outfit_id: sprite.outfit_id ?? outfitId,
      };
      const card = document.createElement('div');
      card.className = 'sprite-comparison-item sprite-item--missing';
      card.setAttribute('role', 'radio');
      card.tabIndex = 0;
      card.dataset.key = spriteKey(item);
      const img = document.createElement('img');
      img.className = 'sprite-comparison-image';
      img.alt = formatSpriteNameForDisplay(item.actionName || item.action_name) || 'action';
      img.loading = 'lazy';
      img.src = `${CDN_URL}${getSpriteSmallImagePath(item)}`;
      const label = document.createElement('div');
      label.className = 'sprite-comparison-label';
      label.textContent = img.alt;
      card.append(img, label);
      const handleSelect = () => {
        selectedSprite = item;
        selectedGalleryKey = null;
        els.spriteGrid.querySelectorAll('.sprite-comparison-item').forEach((node) => {
          const on = node === card;
          node.classList.toggle('sprite-item--selected', on);
          node.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        updateCredits(els);
      };
      card.addEventListener('click', handleSelect);
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        handleSelect();
      });
      els.spriteGrid.appendChild(card);
    });
    if (els.spriteStatus) {
      els.spriteStatus.textContent = sprites.length
        ? getT('free_video_sprites_ready', '{count} images ready').replace('{count}', String(sprites.length))
        : getT('free_video_sprites_empty', 'No ready images for this outfit');
    }
  } catch (error) {
    console.error('[freeVideoFlow] sprites failed', error);
    if (els.spriteStatus) els.spriteStatus.textContent = getT('error', 'Could not load images.');
  }
};

const handleSubmit = async (els) => {
  const prompt = els.prompt?.value?.trim();
  if (!prompt) {
    showError(getT('free_image_prompt_required', 'Please enter a prompt'));
    return;
  }
  if (prompt.length > FREE_VIDEO_PROMPT_MAX) {
    showError(getT('free_video_prompt_too_long', 'Prompt is too long ({count}/{max} characters).')
      .replace('{count}', String(prompt.length))
      .replace('{max}', String(FREE_VIDEO_PROMPT_MAX)));
    return;
  }
  const token = await getAuthToken();
  if (!token) {
    await showFreeAccountModalIfNeeded();
    return;
  }
  const seed = videoSeedControl?.readSeed() ?? null;
  if (seed == null) {
    showError(getT('seed_invalid', 'Enter a seed between 1 and 2147483647'));
    return;
  }
  if (videoLoras?.overLimit()) {
    showError(videoLoras.limitMessage());
    return;
  }
  let lora = null;
  try {
    lora = readVideoLora({ strict: true });
  } catch (error) {
    showError(error.message || getT('video_lora_id_required', 'Please provide a LoRA ID.'));
    return;
  }
  const cost = computeVideoUnitCost({
    operation: 'video_create',
    userVideoLora: lora,
    maxLoras: videoLoraCap(sourceState),
    durationSeconds: readDurationSeconds(),
  });
  const creditCheck = buildLocalCreditResult(cost);
  if (!creditCheck.has_enough_credits) {
    showError(getT('free_image_insufficient_credits', 'Insufficient credits'));
    return;
  }

  let body = {
    prompt,
    lora,
    source_kind: sourceState,
    seed,
    loop: readLoopClip(),
    duration_seconds: readDurationSeconds(),
  };
  try {
    if (sourceState === VIDEO_SOURCES.SPRITE) {
      if (!selectedSprite) throw new Error(getT('free_video_pick_sprite', 'Select a sprite image'));
      body.character_id = selectedSprite.char_id;
      body.outfit_id = selectedSprite.outfit_id;
      body.action_id = selectedSprite.action_id;
    } else {
      if (!appliedFrame || !appliedFrameSize) throw new Error(getT('free_video_crop_required', 'Apply the crop first'));
      const blob = await dataUrlToBlob(appliedFrame);
      const uploaded = await uploadStandaloneVideoFrame({ token, blob });
      body.first_frame_key = uploaded.frame_key || uploaded.first_frame_key;
      body.width = appliedFrameSize.width;
      body.height = appliedFrameSize.height;
      if (sourceState === VIDEO_SOURCES.GALLERY) body.source_image_output_key = selectedGalleryKey;
    }
  } catch (error) {
    showError(sfwModerationMessage(error) || error.message || getT('free_video_source_required', 'Choose a source image'));
    return;
  }

  els.submit?.classList.add('is-busy');
  els.submit.disabled = true;
  try {
    const queued = await scheduleStandaloneVideo({ token, body });
    await syncSpriteCredits();
    videoSeedControl?.rollAfterQueue();
    showSuccess(getT('free_video_queued', 'Video queued for generation'));
    els.previewSection?.classList.remove('hidden');
    els.previewSection?.setAttribute('aria-hidden', 'false');
    waitAbort?.abort();
    waitAbort = new AbortController();
    const onEstimate = startVideoEta(els, queued.output_key);
    await waitForQueuedVideo({
      outputKey: queued.output_key,
      pendingUrl: queued.pendingUrl,
      signal: waitAbort.signal,
      onEstimate,
    });
    const objectUrl = await fetchStandaloneVideoObjectUrl({ token, outputKey: queued.output_key, bust: true });
    const videoEl = els.previewCard?.querySelector('video');
    if (videoEl && objectUrl) {
      videoEl.src = objectUrl;
      videoEl.classList.remove('hidden');
    }
    stopVideoEta(els);
    showSuccess(getT('free_video_ready', 'Video ready'));
  } catch (error) {
    stopVideoEta(els);
    if (error?.name === 'AbortError') return;
    showError(sfwModerationMessage(error) || error.message || getT('free_video_failed', 'Failed to create video'));
  } finally {
    els.submit?.classList.remove('is-busy');
    updateCredits(els);
  }
};

const handleFreeVideoWand = async (els) => {
  if (!hasFreeVideoSource()) return;
  const token = await getAuthToken();
  if (!token) {
    await showFreeAccountModalIfNeeded();
    return;
  }
  const accepted = await openFreeVideoWandModal({
    onGenerate: async ({ idea }) => {
      els.wand?.classList.add('is-busy');
      if (els.wand) els.wand.disabled = true;
      try {
        const payload = await requestAiGenerateField({
          field: 'freeVideoPrompt',
          instruction: idea,
          source_kind: sourceState,
          action_id: sourceState === VIDEO_SOURCES.SPRITE ? selectedSprite?.action_id : undefined,
          source_image_output_key: sourceState === VIDEO_SOURCES.GALLERY ? selectedGalleryKey : undefined,
          loop: readLoopClip(),
        }, token);
        return typeof payload?.value === 'string' ? payload.value.trim() : '';
      } finally {
        els.wand?.classList.remove('is-busy');
        updateCredits(els);
      }
    },
  });
  if (!accepted?.prompt || !els.prompt) return;
  els.prompt.value = accepted.prompt;
  els.prompt.dispatchEvent(new Event('input', { bubbles: true }));
};

export const initFreeVideoFlow = ({
  initialKind = VIDEO_KINDS.SPRITE,
  initialSource = VIDEO_SOURCES.GALLERY,
  outputKey = '',
} = {}) => {
  const els = queryEls();
  if (!els.freePanel) return;
  document.addEventListener('translationsApplied', (event) => {
    if (event.detail?.page !== 'create-video') return;
    updateCredits(els);
  });
  crop = bindMediaCropPanel({
    viewportEl: els.cropViewport,
    imageEl: els.cropImage,
    zoomEl: els.cropZoom,
    outputWidth: 416,
    outputHeight: 544,
    mimeType: 'image/webp',
    quality: 0.92,
  });

  setVideoKind(els, initialKind);
  sourceState = initialSource || VIDEO_SOURCES.GALLERY;
  setVideoSource(els, sourceState);

  const setGalleryPickerLabel = (picked) => {
    if (!els.galleryPickerLabel) return;
    els.galleryPickerLabel.textContent = picked
      ? getT('free_video_image_selected', 'HD image selected')
      : getT('free_video_picker_placeholder', 'Pick an HD image');
  };

  attachFreeVideoImagePickerToTrigger({
    triggerEl: els.galleryPickerTrigger,
    getSelectedKey: () => selectedGalleryKey,
    onSelect: async (item) => {
      if (!item?.is_hd) return;
      selectedGalleryKey = item.output_key;
      selectedSprite = null;
      setGalleryPickerLabel(true);
      try {
        const token = await getAuthToken();
        const objectUrl = await fetchStandaloneImageObjectUrl({ token, outputKey: item.output_key });
        if (objectUrl) await showCropInSection(els, VIDEO_SOURCES.GALLERY, objectUrl);
      } catch (error) {
        reportCropLoadError(error);
        hideCrop(els, { clearApplied: true });
      }
      updateCredits(els);
    },
  });

  els.kindTabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const kind = tab.id === 'video-kind-free' ? VIDEO_KINDS.FREE : VIDEO_KINDS.SPRITE;
      setVideoKind(els, kind);
      if (kind === VIDEO_KINDS.FREE) {
        applyTranslations('create-video', getCurrentLanguage());
        updateCredits(els);
      }
    });
  });
  els.sourceTabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      sourceState = tab.id.replace('video-source-', '');
      selectedSprite = null;
      hideCrop(els, { clearApplied: true });
      if (sourceState !== VIDEO_SOURCES.GALLERY) {
        selectedGalleryKey = null;
        setGalleryPickerLabel(false);
      }
      setVideoSource(els, sourceState);
      updateCredits(els);
    });
  });
  els.charSelect?.addEventListener('change', () => {
    selectedSprite = null;
    void loadFreeSprites(els);
    updateCredits(els);
  });
  els.outfitSelect?.addEventListener('change', () => {
    selectedSprite = null;
    void loadFreeSprites(els);
    updateCredits(els);
  });
  els.spriteSearch?.addEventListener('input', () => {
    const q = els.spriteSearch.value.trim().toLowerCase();
    els.spriteGrid?.querySelectorAll('.sprite-comparison-item').forEach((item) => {
      const label = item.querySelector('.sprite-comparison-label')?.textContent?.toLowerCase() || '';
      item.hidden = Boolean(q) && !label.includes(q);
    });
  });
  els.uploadInput?.addEventListener('change', async () => {
    const file = els.uploadInput.files?.[0];
    if (!file) return;
    selectedSprite = null;
    selectedGalleryKey = null;
    const objectUrl = URL.createObjectURL(file);
    try {
      await showCropInSection(els, VIDEO_SOURCES.UPLOAD, objectUrl, { ownedBlob: true });
    } catch (error) {
      reportCropLoadError(error);
      hideCrop(els, { clearApplied: true });
    }
    updateCredits(els);
  });
  els.cropApply?.addEventListener('click', async () => {
    try {
      await applyCrop(els);
    } catch (error) {
      console.error('[freeVideoFlow] crop apply failed', error);
      showError(getT('free_video_crop_failed', 'Failed to apply cropped image.'));
    }
    updateCredits(els);
  });
  els.cropCancel?.addEventListener('click', () => {
    hideCrop(els, { clearApplied: true });
    updateCredits(els);
  });
  els.prompt?.addEventListener('input', () => updateCredits(els));
  syncDurationOptions();
  document.getElementById('free-video-duration')?.addEventListener('change', () => updateCredits(els));
  setupCharacterCount('camera-text-free-video', 'camera-text-free-video-counter', FREE_VIDEO_PROMPT_MAX);
  els.wand?.addEventListener('click', () => { void handleFreeVideoWand(els); });
  videoLoras = mountVideoLoraRows({
    rowsEl: els.loraRows,
    addButton: els.addLora,
    titleEl: els.loraTitle,
    max: videoLoraCap(sourceState),
    onChange: () => updateCredits(els),
    text: getT,
  });
  videoSeedControl = bindGenerationSeedControl({
    input: els.seed,
    lockButton: els.seedLock,
    rerollButton: els.seedReroll,
    lockedLabel: getT('seed_lock', 'Keep seed after generation'),
    unlockedLabel: getT('seed_randomize_after', 'Unlock to pick a new seed after each generation.'),
  });
  els.submit?.addEventListener('click', () => handleSubmit(els));

  const openFreeVideoAdvanced = () => {
    const details = document.getElementById('free-video-advanced-lora');
    if (details) details.open = true;
  };

  const applySimilarPrompt = (payload) => {
    if (!payload || typeof payload.prompt !== 'string') return;
    setVideoKind(els, VIDEO_KINDS.FREE);
    if (els.prompt) {
      els.prompt.value = payload.prompt;
      els.prompt.dispatchEvent(new Event('input', { bubbles: true }));
    }
    const loras = Array.isArray(payload.loras)
      ? payload.loras
      : (payload.lora && typeof payload.lora === 'object' ? [payload.lora] : []);
    if (loras.length && videoLoras) {
      videoLoras.setEntries(loras);
      openFreeVideoAdvanced();
    }
  };

  const handoff = consumeSimilarStandaloneVideo();
  const galleryKey = outputKey || handoff?.output_key;
  if (galleryKey) {
    setVideoKind(els, VIDEO_KINDS.FREE);
    sourceState = VIDEO_SOURCES.GALLERY;
    setVideoSource(els, sourceState);
    selectedGalleryKey = galleryKey;
    setGalleryPickerLabel(true);
    void (async () => {
      try {
        const token = await getAuthToken();
        const objectUrl = await fetchStandaloneImageObjectUrl({ token, outputKey: galleryKey });
        if (objectUrl) await showCropInSection(els, VIDEO_SOURCES.GALLERY, objectUrl);
      } catch (error) {
        reportCropLoadError(error);
        hideCrop(els, { clearApplied: true });
      }
      updateCredits(els);
    })();
  }
  applySimilarPrompt(handoff);
  const videoSeedFromUrl = initialKind === VIDEO_KINDS.FREE
    ? new URLSearchParams(window.location.search).get('seed')
    : null;
  const linkedSeed = handoff?.seed ?? videoSeedFromUrl;
  if (parseGenerationSeed(linkedSeed) != null && videoSeedControl?.applySeed(linkedSeed)) {
    setVideoKind(els, VIDEO_KINDS.FREE);
    openFreeVideoAdvanced();
  }
  updateCredits(els);
};
